import { isProdEnvIndeed } from '../environment';
import { BiEvent, BiEventName, SIGN_UP_EVENTS, USER_EVENTS } from './events';
import mixpanel, { Mixpanel } from 'mixpanel-browser';

const isEventToSendToWeb = (eventName: BiEventName) => {
  const eventsToSendToWeb: BiEventName[] = [
    SIGN_UP_EVENTS.newUserLogin,
    USER_EVENTS.meetingBooked,
    USER_EVENTS.meetingBooked,
  ];
  return eventsToSendToWeb.includes(eventName);
};

//This is to indicate which BI services are should be enabled.
const ENABLE_MAP = {
  mixpanel: false,
};

export type ClearbitProps = {
  CB_title?: string;
  CB_role?: string;
  CB_seniority?: string;
  CB_location?: string;
  CB_linkedin?: string;
  CB_companyName?: string;
  CB_domain?: string;
  CB_industry?: string;
  CB_subIndustry?: string;
  CB_sector?: string;
  CB_industryGroup?: string;
  CB_employees?: string;
  CB_revenue?: string;
  CB_foundedYear?: string;
  CB_company_location?: string;
  CB_company_linkedin?: string;
  CB_tech?: string;
};

export type BiUser = {
  id: string;
  email: string;
  name: string;
  role: string;
  accountId?: string;
  accountName?: string;
  accountType?: string;
  questionnaireRole?: string;
  questionnaireUseCases?: string;
  userSource?: string;
};

let mixpanelApp: Mixpanel | undefined;
let mixpanelWeb: Mixpanel | undefined;

const initMixpanel = () => {
  mixpanelApp = mixpanel.init(
    'a51065b3a9d0329648a8c5545173ae11',
    {
      track_pageview: 'url-with-path',
      persistence: 'localStorage',
      ignore_dnt: true,
      api_host: 'https://bi.sweep.io',
    },
    'app',
  );
  mixpanelWeb = mixpanel.init(
    'dbcc0dc209419a071fa40a0416ef5d78',
    {
      track_pageview: false,
      persistence: 'localStorage',
      ignore_dnt: true,
      api_host: 'https://bi.sweep.io',
    },
    'website',
  );
};

const setUserInMixpanel = (
  user: BiUser,
  utmParams: Record<string, any>,
  clearbitProps: ClearbitProps,
) => {
  if (mixpanelApp) {
    mixpanelApp.identify(user.id);
    mixpanelApp.alias(user.email);
    mixpanelApp.people.set({
      $name: user.name,
      $email: user.email,
      role: user.role,
      accountId: user.accountId,
      accountName: user.accountName,
      plan: user.accountType,
      questionnaireRole: user.questionnaireRole,
      questionnaireUseCases: user.questionnaireUseCases,
      ...clearbitProps,
      ...utmParams,
    });
  }
  if (mixpanelWeb) {
    mixpanelWeb.identify(user.email);
    mixpanelWeb.people.set({
      $email: user.email,
      plan: user.accountType,
      'User Source': user.userSource,
    });
  }
};

const setUserSourceInMixpanel = (userSource: string) => {
  if (mixpanelWeb) {
    mixpanelWeb.people.set({
      'User Source': userSource,
    });
  }
};

const logoutUserInMixpanel = () => {
  if (!mixpanelApp) return;
  mixpanelApp.reset();
};

const sendEventToWeb = (event: BiEvent) => {
  if (!mixpanelWeb) return;
  mixpanelWeb.track(event.name, event.props);
};

const sendEvent = (event: BiEvent) => {
  if (isEventToSendToWeb(event.name)) {
    sendEventToWeb(event);
  }
  if (!mixpanelApp) return;
  mixpanelApp.track(event.name, event.props);
};

export const initBi = (force = false) => {
  if (isProdEnvIndeed() || force) {
    ENABLE_MAP.mixpanel = true;
  }
  if (ENABLE_MAP.mixpanel) initMixpanel();
};

export const setUserDetailsForBi = (
  user: BiUser,
  utmParams: Record<string, any>,
  clearbitProps: ClearbitProps,
) => {
  if (ENABLE_MAP.mixpanel) setUserInMixpanel(user, utmParams, clearbitProps);
};

export const setUserSignUpSource = (userSource: string) => {
  if (ENABLE_MAP.mixpanel) setUserSourceInMixpanel(userSource);
};

export const sendEventInMixpanel = (event: BiEvent) => {
  if (ENABLE_MAP.mixpanel) sendEvent(event);
};

export const logoutUserBI = () => {
  if (ENABLE_MAP.mixpanel) logoutUserInMixpanel();
};
